<template>
  <v-card class="pa-4 mx-auto w-100">
    <h2 class="text-h5 mb-1">Welcome! Let’s get set up</h2>
    <p class="text-body-2 text-medium-emphasis mb-4">
      We’ll guide you through the required steps to configure this account’s inventory behavior.
    </p>

    <v-stepper :model-value="activeProgressStep" class="onboarding-stepper">
        <!-- 1) Header -->
        <v-stepper-header>
          <v-stepper-item
            value="select-data-source"
            title="Select Data Source"
            :complete="!showWelcome"
            :editable="maxReachedStep >= 1"
            :class="stepperItemClass('select-data-source')"
            @click="goToDataSourceSelection"
          />
          <v-divider />
          <template v-if="showFeedSteps">
            <v-stepper-item
              value="feed-source-mapping"
              title="Feed Source Mapping"
              :complete="step > 1"
              :editable="!showWelcome && maxReachedStep >= 1"
              :class="stepperItemClass('feed-source-mapping')"
              @click="goToFeedSourceMapping"
            />
            <v-divider />
            <v-stepper-item
              value="map-fields"
              title="Map Fields"
              :complete="step > 2"
              :editable="!showWelcome && maxReachedStep >= 2"
              :class="stepperItemClass('map-fields')"
              @click="goToReachedStep(2)"
            />
            <v-divider />
          </template>
          <v-stepper-item
            value="set-up-segments"
            title="Set Up Segments"
            :complete="step > 3"
            :editable="!showWelcome && maxReachedStep >= 3"
            :class="stepperItemClass('set-up-segments')"
            @click="goToReachedStep(3)"
          />
          <v-divider />
          <v-stepper-item
            value="define-publish-rules"
            title="Define Publish Rules"
            :complete="step > 4"
            :editable="!showWelcome && maxReachedStep >= 4"
            :class="stepperItemClass('define-publish-rules')"
            @click="goToReachedStep(4)"
          />
          <v-divider />
          <v-stepper-item
            value="finish-setup"
            title="Finish"
            :editable="!showWelcome && maxReachedStep >= 5"
            :class="stepperItemClass('finish-setup')"
            @click="goToReachedStep(5)"
          />
        </v-stepper-header>

        <!-- 2) Content -->
        <div class="mt-4">
          <!-- 1) Data source selection / feed-source matrix -->
          <div v-if="step === 1">
            <template v-if="showWelcome">
              <ConfigPageShell
                title="Select Data Source"
                subtitle="Choose how this account will receive inventory and set the default website visibility."
                :show-back-to-setup="false"
                dense
              >
                <template #actions>
                  <v-btn
                    color="primary"
                    variant="flat"
                    data-testid="onboarding-welcome-continue"
                    :disabled="useFeedChoice == null || defaultWebsiteVisibility == null"
                    @click="continueFromWelcome"
                  >
                    Next
                  </v-btn>
                </template>

                <div class="text-body-2 font-weight-medium mb-2">You’ll complete:</div>
                <v-list density="compact" class="pa-0" role="list">
                  <v-list-item class="px-0" role="listitem">
                    <template #prepend><v-icon size="18">mdi-check-circle-outline</v-icon></template>
                    <v-list-item-title class="text-body-2">Select Data Source</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="showFeedSteps" class="px-0" role="listitem">
                    <template #prepend><v-icon size="18">mdi-check-circle-outline</v-icon></template>
                    <v-list-item-title class="text-body-2">Feed Source Mapping</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="showFeedSteps" class="px-0" role="listitem">
                    <template #prepend><v-icon size="18">mdi-check-circle-outline</v-icon></template>
                    <v-list-item-title class="text-body-2">Map Fields</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-0" role="listitem">
                    <template #prepend><v-icon size="18">mdi-check-circle-outline</v-icon></template>
                    <v-list-item-title class="text-body-2">Set Up Segments</v-list-item-title>
                  </v-list-item>
                  <v-list-item class="px-0" role="listitem">
                    <template #prepend><v-icon size="18">mdi-check-circle-outline</v-icon></template>
                    <v-list-item-title class="text-body-2">Define Publish Rules</v-list-item-title>
                  </v-list-item>
                </v-list>

                <v-divider class="my-4" />

                <div class="text-body-2 font-weight-medium mb-2">Will this account use an import feed?</div>
                <div class="text-body-2 text-medium-emphasis mb-2">
                  If yes, you’ll select primary feed source(s) and map fields. If no, you can skip those steps
                  and configure Groups + Publish Rules only.
                </div>
                <v-radio-group v-model="useFeedChoice" class="mt-2" hide-details>
                  <v-radio :value="true" label="Yes — we will use an import feed" />
                  <v-radio :value="false" label="No — manual-only inventory (no import feed)" />
                </v-radio-group>

                <v-divider class="my-4" />

                <div class="text-body-2 font-weight-medium mb-2">
                  Do you want to show vehicles on the website by default?
                </div>
                <div class="text-body-2 text-medium-emphasis mb-2">
                  Sets the default website visibility for all new vehicles entering this account's inventory.
                  You can override it individually per vehicle at any time.
                </div>
                <v-radio-group v-model="defaultWebsiteVisibility" class="mt-2" hide-details>
                  <v-radio :value="true" label="Yes — show vehicles on the website by default" />
                  <v-radio :value="false" label="No — hide vehicles from the website by default" />
                </v-radio-group>
              </ConfigPageShell>
            </template>

            <template v-else-if="showFeedSteps">
              <FeedPerStateEditor
                mode="onboarding"
                :require-selection="true"
                @saved="handleFeedMappingSaved"
                @continue="handleFeedMappingContinue"
              />
            </template>
          </div>

          <!-- 2) Mapping -->
          <div v-if="showFeedSteps && step === 2">
            <template v-if="!hasPrimarySources">
              <div class="d-flex align-start justify-space-between ga-3 mb-4">
                <div>
                  <div class="text-subtitle-1 font-weight-bold mb-1">Map Fields</div>
                  <div class="text-body-2 text-medium-emphasis">
                    Field mapping is unavailable because no primary import feeds are selected.
                  </div>
                </div>
                <v-btn color="primary" variant="flat" data-testid="onboarding-skip-field-mapping" @click="advanceTo(3)">
                  Continue
                </v-btn>
              </div>
              <v-alert type="info" variant="tonal" class="mb-4">
                No primary import feeds selected. Field mapping is unavailable.
              </v-alert>
            </template>

            <VehicleFieldMapping v-else @done="advanceTo(3)" :is-on-boarding="true" segment="all" />
          </div>

          <!-- 3) Segmentation -->
          <div v-if="step === 3">
            <InventorySegmentation @done="advanceTo(4)" :is-on-boarding="true" />
          </div>

          <!-- 4) Listing / DePublish rules -->
          <div v-if="step === 4">
            <VehicleListingSettings @done="advanceTo(5)" :is-on-boarding="true" />
          </div>

          <!-- 5) Finish -->
          <div v-if="step === 5">
            <ConfigPageShell
              title="Finish Setup"
              subtitle="Review what happens next and complete onboarding."
              :show-back-to-setup="false"
              dense
            >
              <template #actions>
                <v-btn
                  color="primary"
                  variant="flat"
                  :loading="isFinishing"
                  data-testid="onboarding-finish-complete"
                  @click="completeOnboarding"
                >
                  Finish
                </v-btn>
              </template>

              <v-alert
                v-if="finishError"
                type="error"
                variant="tonal"
                class="mb-4"
                data-testid="onboarding-finish-error"
              >
                {{ finishError }}
              </v-alert>

              <v-alert type="info" variant="tonal" class="mb-4">
                We will initiate a <strong>Publish</strong> after onboarding completes to promote your
                configuration changes.
              </v-alert>

              <div class="text-body-2 text-medium-emphasis mb-4">
                If your inventory feed is available, processing will begin automatically.
              </div>

              <v-list density="compact" class="pa-0" role="list">
                <v-list-item>
                  <template #prepend>
                    <v-icon color="success" size="20">mdi-check-circle-outline</v-icon>
                  </template>
                  <v-list-item-title>Data source selected</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="showFeedSteps">
                  <template #prepend>
                    <v-icon color="success" size="20">mdi-check-circle-outline</v-icon>
                  </template>
                  <v-list-item-title>Feed source mapping configured</v-list-item-title>
                </v-list-item>

                <v-list-item v-if="showFeedSteps">
                  <template #prepend>
                    <v-icon color="success" size="20">mdi-check-circle-outline</v-icon>
                  </template>
                  <v-list-item-title>Field mapping reviewed</v-list-item-title>
                </v-list-item>

                <v-list-item>
                  <template #prepend>
                    <v-icon color="success" size="20">mdi-check-circle-outline</v-icon>
                  </template>
                  <v-list-item-title>Vehicle groups reviewed</v-list-item-title>
                </v-list-item>

                <v-list-item>
                  <template #prepend>
                    <v-icon color="success" size="20">mdi-check-circle-outline</v-icon>
                  </template>
                  <v-list-item-title>Publish rules reviewed</v-list-item-title>
                </v-list-item>
              </v-list>
            </ConfigPageShell>
          </div>
        </div>
      </v-stepper>

    <v-overlay
      v-model="isPublishing"
      persistent
      scrim="rgba(0, 0, 0, 0.6)"
      class="d-flex align-center justify-center"
    >
      <v-card class="publish-loader-card" elevation="6">
        <v-card-text class="d-flex align-center ga-4">
          <v-progress-circular indeterminate color="primary" size="44" />
          <div>
            <div class="text-subtitle-1 font-weight-medium">Initiating publish…</div>
            <div class="text-caption text-medium-emphasis">This may take a moment.</div>
          </div>
        </v-card-text>
      </v-card>
    </v-overlay>
  </v-card>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useProductSettingsStore } from '@/stores/productSettings'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import InventorySegmentation from '@/views/InventorySegmentation.vue'
import VehicleFieldMapping from '@/views/VehicleFieldMapping.vue'
import VehicleListingSettings from '@/views/VehicleListingSettings.vue'
import FeedPerStateEditor from '@/components/FeedPerStateEditor.vue'
import ConfigPageShell from '@/components/config/ConfigPageShell.vue'
import { fetchFeedMapping } from '@/services/feedMappingService'
import { getUseFeedChoiceKey as buildUseFeedChoiceKey, parseUseFeedChoice } from '@/utils/onboardingFeedChoice'
import {
  getOnboardingUsesImportFeed,
  triggerOnboardingPublish,
} from '@/utils/onboardingCompletion'

const emit = defineEmits<{
  (e: 'finished'): void
  (e: 'step', value: number): void
}>()

const productSettings = useProductSettingsStore()
const accountSettings = useAccountSettingsStore()
const step = ref(1)
// Onboarding step progress is UI-only (resets on refresh).
const maxReachedStep = ref(1)

const showWelcome = ref(true)

const useFeedChoice = ref<boolean | null>(null)
const showFeedSteps = computed(() => useFeedChoice.value === true)
const defaultWebsiteVisibility = ref<boolean | null>(null)

type ProgressStep =
  | 'select-data-source'
  | 'feed-source-mapping'
  | 'map-fields'
  | 'set-up-segments'
  | 'define-publish-rules'
  | 'finish-setup'

const activeProgressStep = computed<ProgressStep>(() => {
  if (step.value === 1 && showWelcome.value) return 'select-data-source'
  if (step.value === 1 && showFeedSteps.value) return 'feed-source-mapping'
  if (step.value === 2) return 'map-fields'
  if (step.value === 3) return 'set-up-segments'
  if (step.value === 4) return 'define-publish-rules'
  return 'finish-setup'
})

const completedProgressSteps = computed<Set<ProgressStep>>(() => {
  const completed = new Set<ProgressStep>()
  if (!showWelcome.value) completed.add('select-data-source')
  if (showFeedSteps.value && step.value > 1) completed.add('feed-source-mapping')
  if (showFeedSteps.value && step.value > 2) completed.add('map-fields')
  if (step.value > 3) completed.add('set-up-segments')
  if (step.value > 4) completed.add('define-publish-rules')
  return completed
})

function stepperItemClass(progressStep: ProgressStep) {
  return {
    'onboarding-step-item': true,
    'onboarding-step-item--active': activeProgressStep.value === progressStep,
    'onboarding-step-item--complete': completedProgressSteps.value.has(progressStep),
    'onboarding-step-item--future':
      activeProgressStep.value !== progressStep && !completedProgressSteps.value.has(progressStep),
  }
}

const effectivePrimarySources = ref<string[]>([])
const hasPrimarySources = computed(() => effectivePrimarySources.value.length > 0)
const isFinishing = ref(false)
const isPublishing = ref(false)
const finishError = ref('')
let primarySourcesLoadSeq = 0

function getUseFeedChoiceKey(): string {
  return buildUseFeedChoiceKey(accountSettings.accountId)
}

function applyFeedChoice(choice: boolean) {
  useFeedChoice.value = choice
  // Start at the beginning of the selected flow.
  if (choice) {
    step.value = 1
    maxReachedStep.value = 1
  } else {
    step.value = 3
    maxReachedStep.value = 3
  }
}

async function continueFromWelcome() {
  if (useFeedChoice.value == null || defaultWebsiteVisibility.value == null) return
  try {
    sessionStorage.setItem(getUseFeedChoiceKey(), useFeedChoice.value ? '1' : '0')
  } catch {
    // ignore
  }
  // Persist the account-level default_website_visibility to JAMS.
  // Fire-and-forget; onboarding should not block on this.
  productSettings.saveSettings({ default_website_visibility: defaultWebsiteVisibility.value }).catch(() => {})
  applyFeedChoice(useFeedChoice.value)
  showWelcome.value = false
}

function goToDataSourceSelection() {
  step.value = 1
  showWelcome.value = true
}

function goToFeedSourceMapping() {
  if (showWelcome.value || !showFeedSteps.value) return
  step.value = 1
  showWelcome.value = false
}

function goToReachedStep(targetStep: number) {
  if (showWelcome.value || maxReachedStep.value < targetStep) return
  step.value = targetStep
}

function handleFeedMappingSaved(value: { derivedPrimarySources?: string[] }) {
  primarySourcesLoadSeq += 1
  effectivePrimarySources.value = Array.isArray(value?.derivedPrimarySources)
    ? value.derivedPrimarySources
    : []
}

async function handleFeedMappingContinue() {
  if (effectivePrimarySources.value.length === 0) {
    await refreshPrimarySourcesState()
  }
  advanceTo(2)
}

async function refreshPrimarySourcesState() {
  const seq = ++primarySourcesLoadSeq

  try {
    const endpoint = import.meta.env.VITE_ISE_BACKEND_URL
    const jazelId = accountSettings.accountId
    if (!endpoint || !jazelId) {
      if (seq === primarySourcesLoadSeq) {
        effectivePrimarySources.value = []
      }
      return
    }

    const token = String(accountSettings.sessionToken || '').trim()
    const res = await fetchFeedMapping(endpoint, jazelId, { token })

    if (seq !== primarySourcesLoadSeq) return

    effectivePrimarySources.value = Array.isArray(res?.derivedPrimarySources)
      ? res.derivedPrimarySources
      : []
  } catch {
    if (seq === primarySourcesLoadSeq) {
      effectivePrimarySources.value = []
    }
  }
}

onMounted(() => {
  // Always show welcome on refresh (do not resume onboarding UI).
  showWelcome.value = true

  // Resolve feed choice (session-scoped).
  try {
    const raw = sessionStorage.getItem(getUseFeedChoiceKey())
    useFeedChoice.value = parseUseFeedChoice(raw)
  } catch {
    useFeedChoice.value = null
  }

  // Draft-first check so step 2 gating works even when primary sources are only saved as a draft.
  refreshPrimarySourcesState()
})

watch(step, (val) => {
  console.log('#### STEP CHANGED', val);
  emit('step', val)
})

function advanceTo(nextStep: number) {
  step.value = nextStep
  maxReachedStep.value = Math.max(maxReachedStep.value, nextStep)
}

function finishOnboarding() {
  emit('finished')
}

async function completeOnboarding() {
  if (isFinishing.value) return

  isFinishing.value = true
  isPublishing.value = true
  finishError.value = ''

  try {
    const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
    const accountId = accountSettings.accountId

    if (!iseEndpoint || !accountId) {
      throw new Error(
        'Onboarding could not be completed because account or backend configuration is missing.',
      )
    }

    const usesImportFeed = getOnboardingUsesImportFeed(accountId)
    await triggerOnboardingPublish({
      iseEndpoint,
      accountId,
      usesImportFeed,
    })

    finishOnboarding()
  } catch (error: any) {
    finishError.value =
      error?.message ||
      'Onboarding could not be completed because publish could not be started. Please try again.'
  } finally {
    isFinishing.value = false
    isPublishing.value = false
  }
}
</script>

<style scoped>
/* Onboarding uses a stepper header that defaults to showing numeric badges.
   Hide the badges so the tabs show only the titles. */
:deep(.v-stepper-item__avatar) {
  display: none !important;
}

:deep(.onboarding-step-item .v-stepper-item__title) {
  color: rgba(var(--v-theme-on-surface), 0.58);
  font-weight: 500;
}

:deep(.onboarding-step-item--active .v-stepper-item__title) {
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
}

:deep(.onboarding-step-item--complete .v-stepper-item__title) {
  color: rgba(var(--v-theme-on-surface), 0.72);
}

.publish-loader-card {
  width: min(520px, calc(100vw - 32px));
  border-radius: 12px;
}
</style>
